import React, { useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiGallection } from '@wix/da-types-papi';
import { ALL_FOLDER_ID, SCRAPS_FOLDER_ID } from '@wix/da-url';
import { useFlag } from '@wix/da-shared-react/pkg/redux/flags/hooks/useFlag';
import Flex from '@wix/da-shared-react/pkg/Flex';
import CollapsibleBlock from '@wix/da-shared-react/pkg/CollapsibleBlock';
import Button from '@wix/da-shared-react/pkg/Button';
import SubfolderCard from '../../SubfolderCard';
import { MobileContext } from '@wix/da-shared-react/pkg/Context';

import s from './FolderDescription.scss';

export interface Props {
  folder: PapiGallection;
  subFolders: PapiGallection[];
  className?: string;
}

const COLLAPSE_WHEN_THIS_TALL = 78;

const getDescriptionText = (folder: PapiGallection) => {
  if ([ALL_FOLDER_ID, SCRAPS_FOLDER_ID].includes(folder.folderId)) {
    // pseudo-folders
    return '';
  }

  // Text without line breaks and html tags
  return (folder.description || '').replace(/<\/?[^>]+>/g, '');
};

// TODO: rename to FolderDescriptionCard
export const FolderDescription: React.FC<Props> = ({
  folder,
  subFolders,
  className,
}) => {
  const isMobile = useContext(MobileContext);
  const { t } = useTranslation();
  const forceDesktopFolderDropdown = useFlag('force_desktop_folder_dropdown');

  if (!folder) {
    return null;
  }

  const description = getDescriptionText(folder);

  if (isMobile && !description && forceDesktopFolderDropdown) {
    return null;
  }

  const renderDescription = () => {
    if (!description) {
      return null;
    }

    return (
      <CollapsibleBlock
        maxHeight={COLLAPSE_WHEN_THIS_TALL}
        className={s['collapsible-block']}
        showFade
        fadeClassName={s['collapsible-fade']}
        renderToggleButton={({ collapsed, onClick }) => (
          <Button
            view="b3"
            className={s['collapsible-button']}
            onClick={onClick}
          >
            {t(
              collapsed
                ? 'pages.gallections.folderDescription.readMore'
                : 'common.showLess'
            )}
          </Button>
        )}
      >
        <p className={s['description']}>{description}</p>
      </CollapsibleBlock>
    );
  };

  const renderSubfolders = () => {
    if (!subFolders.length) {
      return null;
    }
    return (
      <div className={s['subfolders-section']}>
        <div className={s['subfolders-header']}>
          {t('pages.gallections.subfolders')}
        </div>
        <Flex wrap="wrap">
          {subFolders.map(subFolder => (
            <SubfolderCard
              key={subFolder.folderId}
              subfolder={subFolder}
              className={s['subfolder']}
            />
          ))}
        </Flex>
      </div>
    );
  };

  return (
    <div
      className={classnames(
        s['root'],
        forceDesktopFolderDropdown && s['force-desktop-folder-dropdown'],
        className
      )}
    >
      <Flex direction="column">
        {!forceDesktopFolderDropdown && isMobile && (
          <h2 className={s['title']}>{folder.name}</h2>
        )}
        {renderDescription()}
        {renderSubfolders()}
      </Flex>
    </div>
  );
};
FolderDescription.displayName = 'FolderDescription';

export default FolderDescription;
